import {useMemo} from 'react';

import {
  Chart as ChartJS,
  Filler,
  Legend,
  LineElement,
  PointElement,
  RadialLinearScale,
  Tooltip,
} from 'chart.js';
import {Radar} from 'react-chartjs-2';

import {Box} from '@mui/material';

import {
  alignmentShapeDatasetStyles,
  ALIGNMENT_SHAPE_DIMENSION_KEYS,
  buildAlignmentShapeChartOptions,
  mapAlignmentShapeScoresToChartValues,
} from './alignmentShapeChartConfig';
import {WORKFORCE_DASHBOARD_ALIGNMENT_SHAPE_CHART_HEIGHT} from './workforceDashboardLayoutConstants';
import type {WorkforceDashboardAlignmentShapeScores} from '../../types/workforceDashboardTypes';

ChartJS.register(RadialLinearScale, PointElement, LineElement, Filler, Tooltip, Legend);

export interface AlignmentShapeDataset {
  label: string;
  scores: WorkforceDashboardAlignmentShapeScores;
}

export interface Props {
  dimensionLabels: Record<(typeof ALIGNMENT_SHAPE_DIMENSION_KEYS)[number], string>;
  datasets: AlignmentShapeDataset[];
}

export const WorkforceDashboardAlignmentShapeRadar = ({dimensionLabels, datasets}: Props) => {
  const labels = useMemo(
    () => ALIGNMENT_SHAPE_DIMENSION_KEYS.map((key) => dimensionLabels[key]),
    [dimensionLabels]
  );

  const chartData = useMemo(
    () => ({
      labels,
      datasets: datasets.map((dataset, index) => {
        const style = alignmentShapeDatasetStyles[index] ?? alignmentShapeDatasetStyles[0];

        return {
          label: dataset.label,
          data: mapAlignmentShapeScoresToChartValues(dataset.scores),
          borderColor: style.borderColor,
          backgroundColor: style.backgroundColor,
          pointBackgroundColor: style.pointBackgroundColor,
          borderWidth: 2,
          fill: true,
        };
      }),
    }),
    [datasets, labels]
  );

  const options = useMemo(
    () =>
      buildAlignmentShapeChartOptions(labels, (dimensionKey, _value, datasetIndex) => {
        const rawValue = datasets[datasetIndex]?.scores[dimensionKey];
        return rawValue === null || rawValue === undefined ? '—' : `${rawValue}%`;
      }),
    [datasets, labels]
  );

  return (
    <Box sx={{width: '100%', height: WORKFORCE_DASHBOARD_ALIGNMENT_SHAPE_CHART_HEIGHT}}>
      <Radar data={chartData} options={options} />
    </Box>
  );
};
